import apiClient from './apiClient';

/**
 * Get customers (admin)
 * @param {object} params - Filter parameters
 * @returns {Promise} - Promise containing customers
 */
export const getCustomers = async (params = {}) => {
  try {
    const response = await apiClient.get('/admin/customers', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get customer by ID (admin)
 * @param {string} id - Customer ID
 * @returns {Promise} - Promise containing customer
 */
export const getCustomerById = async (id) => {
  try {
    const response = await apiClient.get(`/admin/customers/${id}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Create customer (admin)
 * @param {object} customerData - Customer data
 * @returns {Promise} - Promise containing created customer
 */
export const createCustomer = async (customerData) => {
  try {
    const response = await apiClient.post('/admin/customers', customerData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Update customer (admin)
 * @param {string} id - Customer ID
 * @param {object} customerData - Updated customer data
 * @returns {Promise} - Promise containing updated customer
 */
export const updateCustomer = async (id, customerData) => {
  try {
    const response = await apiClient.put(`/admin/customers/${id}`, customerData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Delete customer (admin)
 * @param {string} id - Customer ID
 * @returns {Promise} - Promise containing success message
 */
export const deleteCustomer = async (id) => {
  try {
    const response = await apiClient.delete(`/admin/customers/${id}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get employees (admin)
 * @param {object} params - Filter parameters
 * @returns {Promise} - Promise containing employees
 */
export const getEmployees = async (params = {}) => {
  try {
    const response = await apiClient.get('/admin/employees', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get employee by ID (admin)
 * @param {string} id - Employee ID
 * @returns {Promise} - Promise containing employee
 */
export const getEmployeeById = async (id) => {
  try {
    const response = await apiClient.get(`/admin/employees/${id}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Create employee (admin)
 * @param {object} employeeData - Employee data
 * @returns {Promise} - Promise containing created employee
 */
export const createEmployee = async (employeeData) => {
  try {
    const response = await apiClient.post('/admin/employees', employeeData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Update employee (admin)
 * @param {string} id - Employee ID
 * @param {object} employeeData - Updated employee data
 * @returns {Promise} - Promise containing updated employee
 */
export const updateEmployee = async (id, employeeData) => {
  try {
    const response = await apiClient.put(`/admin/employees/${id}`, employeeData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Delete employee (admin)
 * @param {string} id - Employee ID
 * @returns {Promise} - Promise containing success message
 */
export const deleteEmployee = async (id) => {
  try {
    const response = await apiClient.delete(`/admin/employees/${id}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Assign employee to customer (admin)
 * @param {string} customerId - Customer ID
 * @param {string} employeeId - Employee ID
 * @returns {Promise} - Promise containing success message
 */
export const assignEmployeeToCustomer = async (customerId, employeeId) => {
  try {
    const response = await apiClient.post(`/admin/customers/${customerId}/employees`, { employeeId });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Remove employee from customer (admin)
 * @param {string} customerId - Customer ID
 * @param {string} employeeId - Employee ID
 * @returns {Promise} - Promise containing success message
 */
export const removeEmployeeFromCustomer = async (customerId, employeeId) => {
  try {
    const response = await apiClient.delete(`/admin/customers/${customerId}/employees/${employeeId}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get customer employees (customer)
 * @param {object} params - Filter parameters
 * @returns {Promise} - Promise containing employees assigned to customer
 */
export const getCustomerEmployees = async (params = {}) => {
  try {
    const response = await apiClient.get('/customer/employees', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get employee profile (employee)
 * @returns {Promise} - Promise containing employee profile
 */
export const getEmployeeProfile = async () => {
  try {
    const response = await apiClient.get('/employee/profile');
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Update employee profile (employee)
 * @param {object} profileData - Updated profile data
 * @returns {Promise} - Promise containing updated profile
 */
export const updateEmployeeProfile = async (profileData) => {
  try {
    const response = await apiClient.put('/employee/profile', profileData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get customer profile (customer)
 * @returns {Promise} - Promise containing customer profile
 */
export const getCustomerProfile = async () => {
  try {
    const response = await apiClient.get('/customer/profile');
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Update customer profile (customer)
 * @param {object} profileData - Updated profile data
 * @returns {Promise} - Promise containing updated profile
 */
export const updateCustomerProfile = async (profileData) => {
  try {
    const response = await apiClient.put('/customer/profile', profileData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Helper function to handle errors
 * @param {object} error - Error object
 * @returns {Error} - Formatted error
 */
const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  if (message) {
    return new Error(message);
  }
  return error;
};